import Breadcrumb from 'Components/UI/Breadcrumb';
import React, { useState, useEffect } from 'react'
import { Button, Card, CardBody, CardTitle, Col, Container, Form, Input, Label, Row, FormFeedback } from "reactstrap";
import * as yup from "yup";
import { useFormik } from "formik";
import Editor from 'Components/Form/Editor/Editor';
import { getCategoryById, updateCategory } from 'helpers/API Calls/Operations/category';
import { useNavigate, useParams } from 'react-router-dom';
import Spinners from "Components/UI/Spinner";
import Select from "react-select";
import { STATUS_OPTIONS } from './data';
import Dropzone from 'Components/Form/DropZone';
import placeholderImage from "assets/images/img.png"
import { convertToBase64 } from './Create';

function Edit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const formik: any = useFormik({
    initialValues: {
      name: '',
      metaTitle: '',
      metaDesc: '', 
      sequence: '', 
      isAddon: false,
      status: null,
      imageFile: null as File | null,
      imageBase64: '',
      imageError: '',
      categoryImage: '', // for storing initial image from server
    },
    validationSchema: yup.object().shape({
      name: yup.string().required('Please enter category name.'),
      metaTitle: yup.string().optional(),
      metaDesc: yup.string().optional(),
      isAddon: yup.boolean(),
      status : yup.object().shape({label: yup.string().required(), value: yup.string().required(),}).nullable().required('Please select status'),
    }),
    onSubmit: async (values: any, { setSubmitting }) => {
      setSubmitting(true);
      const data : any = {
        category_name: values.name,
        meta_title: values.metaTitle,
        meta_description: values.metaDesc,
        description: content,
        sequence: values.sequence,
        status: values.status.value,
        is_addon: values.isAddon ? 1 : 0,
      }
      if(formik.values.imageBase64){
        data.category_image = formik.values.imageBase64; // Use base64 image if available
      }
      const response = await updateCategory(Number(id), data);
      setSubmitting(false);
      if(response.success){
        navigate('/categories');
      }
    },
  });

  useEffect(() => {
    const fetchCategory = async () => {
      if (id) {
        setIsLoading(true);
        const response = await getCategoryById(Number(id));
        if (response.success) {
          const status_label = response.data.status === 'active' ? 'Active' : 'Inactive';
          formik.setValues({
            name: response.data.category_name || '',
            metaTitle: response.data.meta_title || '',
            metaDesc: response.data.meta_description || '',
            status: {label: status_label, value: response.data.status},
            sequence: response.data.sequence || '',
            isAddon: response.data.is_addon === 1 ? true : false
          });
          setContent(response.data.description || '');
          // Set initial image if it exists
          if (response.data.category_image) {
            formik.setFieldValue('categoryImage', response.data.category_image);
          }
        }
      }
      setIsLoading(false);
    };
    fetchCategory();
  }, [id]);


    // Add scroll to error effect
    useEffect(() => {
      if (Object.keys(formik.errors).length > 0 && formik.isSubmitting) {
        const firstErrorKey = Object.keys(formik.errors)[0];
        const element = document.getElementById(firstErrorKey);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
        }
      }
    }, [formik.errors, formik.isSubmitting]);
    
  return (
    <div className="page-content">
    <Container fluid>
      <Breadcrumb items={[{ label: "Categories", link: "/categories" }, {label: 'Edit'}]} />
      {isLoading ? <div className="position-relative vh-100"><Spinners/></div>
        :
      <Card>
        <CardBody>
          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <Col xl="8">
                <CardTitle tag="h4" className='mb-4'>Basic Details</CardTitle>
                <div className="mb-3">
                  <Label htmlFor="name">Category Name <span className="text-danger">*</span></Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Category Name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    invalid={formik.touched.name && formik.errors.name ? true : false}
                  />
                  {formik.errors.name && formik.touched.name ? (
                    <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
                  ) : null}
                </div>

                <div className="mb-3">
                  <Label>Content</Label>
                  <Editor content={content} setContent={setContent} />
                </div>
              </Col>
              <Col xl="4">
                <Row>
                  <CardTitle tag="h4" className='mb-4'>SEO Information</CardTitle>
                  <div className="mb-3">
                    <Label htmlFor="metaTitle">Meta Title</Label>
                    <Input
                      id="metaTitle"
                      name="metaTitle"
                      type="text"
                      placeholder="Meta Title"
                      value={formik.values.metaTitle}
                      onChange={formik.handleChange}
                      invalid={formik.touched.metaTitle && formik.errors.metaTitle ? true : false}
                    />
                    {formik.errors.metaTitle && formik.touched.metaTitle ? (
                      <FormFeedback type="invalid">{formik.errors.metaTitle}</FormFeedback>
                    ) : null}
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="metaDesc">Meta Description</Label>
                    <Input
                      tag="textarea"
                      className="mb-3"
                      id="metaDesc"
                      name="metaDesc"
                      rows={5}
                      placeholder="Category Description"
                      value={formik.values.metaDesc}
                      onChange={formik.handleChange}
                      invalid={formik.touched.metaDesc && formik.errors.metaDesc ? true : false}
                    />
                    {formik.errors.metaDesc && formik.touched.metaDesc ? (
                      <FormFeedback type="invalid">{formik.errors.metaDesc}</FormFeedback>
                    ) : null}
                  </div>
                </Row>
                <Row>
                  <CardTitle tag="h4" className='mb-4'>Settings</CardTitle>
                  <div className="mb-4">
                    <Label className="form-label">Status</Label>
                    <Select
                      name="status"
                      placeholder="Select Status"
                      options={STATUS_OPTIONS}
                      value={formik.values.status}
                      onChange={(value) => formik.setFieldValue("status", value)}
                      onBlur={formik.handleBlur}
                      className={
                        formik.touched.status && formik.errors.status
                          ? "is-invalid"
                          : ""
                      }
                    />
                    {formik.touched.status && formik.errors.status ? (
                      <FormFeedback type="invalid">{formik.errors.status || formik.errors.status}</FormFeedback>
                    ) : null}
                  </div>

                  <div className="mb-3">
                    <Label className="form-label">Category Type</Label>
                    <div className="form-check form-switch mb-3 form-switch-md">
                      <Input
                        type="switch"
                        className="form-check-input"
                        id="isAddon"
                        name="isAddon"
                        checked={formik.values.isAddon}
                        onChange={e => formik.setFieldValue("isAddon", e.target.checked)}
                      />
                      <Label className="form-check-label" htmlFor="isAddon">
                        Mark as Addon
                      </Label>
                    </div>
                  </div>

                  {/* logo */}
                  <div className="mb-3" id="featured-image-section">
                    <Label htmlFor="name-input">Featured Image <span className="text-danger">*</span></Label>
                    <Dropzone
                      accept = {{"image/*": [".png", ".jpeg", ".jpg"]}}
                      onDrop = {async (acceptedFiles) => {
                        const file = acceptedFiles[0];
                        formik.setFieldValue('imageFile', file);
                        formik.setFieldValue('imageError', '');
                        try {
                          const base64 = await convertToBase64(file);
                          formik.setFieldValue('imageBase64', base64);
                        } catch (error) {
                          formik.setFieldValue('imageError', 'Failed to process image');
                        }
                      }}
                      renderContent = {() => (
                        <div className="text-center cursor-pointer">
                          <div className="position-relative d-inline-block" >
                            <div className={`avatar-xl rounded`}>
                              <div className="avatar-title bg-light ">
                                <img
                                  src={formik.values.imageFile ? URL.createObjectURL(formik.values.imageFile) : (formik.values.categoryImage || placeholderImage)}
                                  id="logo-img"
                                  alt=""
                                  className="avatar-xl rounded"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    />
                    {formik.values.imageError && (
                      <div className="text-danger mt-1 small">{formik.values.imageError}</div>
                    )}
                  </div>
                </Row>
              </Col>
            </Row>

            {/* display content here */}
            {/* <div className="mt-5 overflow-hidden" >
              <div dangerouslySetInnerHTML={{ __html: content }} />
            </div> */}

            <div className="d-flex flex-wrap gap-2 mt-3 justify-content-end">
              <Button type="submit" color="primary" disabled={formik.isSubmitting}>
                {formik.isSubmitting ? 'Updating...' : 'Update Category'}
              </Button>
            </div>
          </Form>
        </CardBody>
      </Card>}
  </Container>
</div>
)
}

export default Edit
